import { useEffect, useRef, useState } from "react";
import { getAnchoredRemainingSeconds } from "../utils/assessmentTimeLeft";

interface UseServerAnchoredTimeLeftOptions {
  assessmentId?: string | null;
  isPaused?: boolean;
}

export const useServerAnchoredTimeLeft = (
  serverTimeLeft: number | null | undefined,
  {
    assessmentId = null,
    isPaused = false,
  }: UseServerAnchoredTimeLeftOptions = {}
): number => {
  const anchorRef = useRef<{
    assessmentId: string | null;
    serverTimeLeft: number;
    syncedAtMs: number;
  }>({
    assessmentId: null,
    serverTimeLeft: 0,
    syncedAtMs: Date.now(),
  });
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (typeof serverTimeLeft !== "number") {
      return;
    }

    const normalizedTimeLeft = Math.max(0, serverTimeLeft);
    const anchor = anchorRef.current;
    const assessmentChanged = anchor.assessmentId !== assessmentId;
    const serverValueChanged = anchor.serverTimeLeft !== normalizedTimeLeft;

    if (assessmentChanged || serverValueChanged) {
      anchorRef.current = {
        assessmentId: assessmentId ?? null,
        serverTimeLeft: normalizedTimeLeft,
        syncedAtMs: Date.now(),
      };
    }

    setRemainingSeconds(
      getAnchoredRemainingSeconds(
        anchorRef.current.serverTimeLeft,
        anchorRef.current.syncedAtMs
      )
    );
  }, [assessmentId, serverTimeLeft]);

  useEffect(() => {
    if (isPaused || typeof serverTimeLeft !== "number") {
      return;
    }

    const tick = () => {
      const anchor = anchorRef.current;
      setRemainingSeconds(
        getAnchoredRemainingSeconds(
          anchor.serverTimeLeft,
          anchor.syncedAtMs
        )
      );
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [assessmentId, isPaused, serverTimeLeft]);

  if (typeof serverTimeLeft !== "number") {
    return 0;
  }

  return remainingSeconds;
};
